import { AutoComplete, CheckIconV2, CloseIcon } from '@repo/atomic-components';
import { MouseEvent, KeyboardEvent, FC, useEffect, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { CATEGORY_SUGGESTIONS } from '@/constants';

interface NewSectionAutocompleteProps {
  onSubmit: (valueLocal: string) => void;
  onCancel: () => void;
}

export const NewSectionAutocomplete: FC<NewSectionAutocompleteProps> = ({ onSubmit, onCancel }) => {
  const autocompleteRef = useRef<AutoComplete>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputCustomButtonRef = useRef<HTMLDivElement>(null);
  const [valueLocal, setValueLocal] = useState<string>('');
  const [isTouchedLocal, setIsTouchedLocal] = useState<boolean>(false);
  const [filteredLocal, setFilteredLocal] = useState<string[]>([]);
  const isOnSelectClick = useRef<boolean>(false);
  const isOnButtonClick = useRef<boolean>(false);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const search = (query: string) => {
    const filteredSuggestions = CATEGORY_SUGGESTIONS.filter((name) =>
      name.toLowerCase().includes(query.toLowerCase()),
    ).slice(0, 6);

    setFilteredLocal(filteredSuggestions);
  };

  const handleApprove = (event?: MouseEvent<HTMLButtonElement>) => {
    if (event) {
      event.stopPropagation();
    }
    isOnButtonClick.current = true;
    onSubmit(valueLocal);
  };

  const handleCancel = (event?: MouseEvent<HTMLButtonElement>) => {
    if (event) {
      event.stopPropagation();
    }
    // !!! The same behavior as onBlur
    isOnButtonClick.current = true;
    onCancel();
  };

  const isDisabled = !valueLocal.trim().length;

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event) {
      event.stopPropagation();
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      handleApprove();
    }
  };

  return (
    <div className="m-0 w-full">
      <div className="relative flex w-full items-center">
        <AutoComplete
          ref={autocompleteRef}
          value={valueLocal}
          suggestions={filteredLocal}
          completeMethod={(e) => search(e.query)}
          loadingIcon={<></>}
          onChange={(e) => {
            e.stopPropagation();
            setValueLocal(e.value);
            if (!isTouchedLocal) {
              setIsTouchedLocal(true);
            }
          }}
          onSelect={(e) => {
            e.originalEvent.stopPropagation();
            // !!! Show that onSelect click, not outside component
            isOnSelectClick.current = true;
          }}
          inputRef={inputRef}
          placeholder="Enter new section name"
          dropdown={false}
          className="w-full text-[14px] [&_input::placeholder]:!text-[14px]"
          inputClassName={classNames(
            '!m-0 !w-full !border',
            '!font-sans !text-[14px] !text-black !font-normal placeholder:!text-[14px]',
            '!appearance-none !rounded-sm !border !bg-white !transition-colors !duration-200',
            '!focus:border-fletchers-gray-200 !focus:outline-offset-0 !focus:outline-none',
            '!hover:border-fletchers-gray-200',
            '!py-0 !px-2',
          )}
          panelClassName={classNames(
            'overflow-y-auto !w-48 !max-w-xs',
            '[&_ul]:py-1',
            '[&_li]:text-[14px]',
            '[&_li]:py-1',
            '[&_li]:whitespace-normal [&_li]:break-words [&_li]:px-2',
          )}
          onKeyDown={handleKeyDown}
        />
        <div ref={inputCustomButtonRef} className="absolute inset-y-0 right-0 flex items-center pr-2">
          <button type="button" className="focus:outline-none" onClick={handleApprove} disabled={isDisabled}>
            <CheckIconV2
              className={classNames('text-fletchers-gray-200 hover:text-fletchers-green-500 -translate-y-[1px]', {
                'cursor-pointer': !isDisabled,
                'cursor-not-allowed': isDisabled,
              })}
              width="14"
              height="14"
            />
          </button>
          <button type="button" className="ml-[8px] focus:outline-none" onClick={handleCancel}>
            <CloseIcon
              className="text-fletchers-gray-200 hover:text-fletchers-green-500 cursor-pointer"
              width="11"
              height="11"
            />
          </button>
        </div>
      </div>
    </div>
  );
};
